import { useEffect, useState, useCallback } from 'react';
import { useToast } from '../../hooks/useToast';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Modal from '../../components/common/Modal';
import InitialsAvatar from '../../components/common/InitialsAvatar.jsx';
import InviteUserModal from './InviteUserModal.jsx';
import ListLoadError from '../../components/admin/ListLoadError';
import api from '../../services/api';
import styles from './RolesPage.module.css';

const fullName = (u) => `${u.firstName || ''} ${u.lastName || ''}`.trim();

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

// isActive is the authoritative account state — an active account is "Active" even if a
// stale invite_status lingers as 'pending'. Only inactive accounts surface invite states.
const statusBadge = (u) => {
  if (u.isActive) return { label: 'Active', cls: styles.badgeActive };
  if (u.inviteStatus === 'pending') return { label: 'Invited — awaiting password', cls: styles.badgeInvited };
  if (u.inviteStatus === 'expired') return { label: 'Expired', cls: styles.badgeExpired };
  return { label: 'Inactive', cls: styles.badgeInactive };
};

// A not-yet-accepted invite (inactive + pending/expired token) gets Resend/Remove;
// every other user gets the full Reset/Deactivate-or-Reactivate/Remove action set.
const isPendingInvite = (u) => !u.isActive && (u.inviteStatus === 'pending' || u.inviteStatus === 'expired');

const CONFIRM = {
  deactivate: { title: 'Deactivate User', label: 'Deactivate', variant: 'danger', success: 'User deactivated' },
  remove: { title: 'Remove From Role', label: 'Remove', variant: 'danger', success: 'User removed from role' },
  reset: { title: 'Reset Password', label: 'Send Reset Email', variant: 'primary', success: 'Password reset email sent' },
};

export default function RoleUsersPanel({ role, canEdit, onChanged }) {
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [usersError, setUsersError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busyUserId, setBusyUserId] = useState(null);
  const [confirm, setConfirm] = useState(null); // { kind, user }
  const [showInvite, setShowInvite] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [assignQuery, setAssignQuery] = useState('');
  const [assignResults, setAssignResults] = useState([]);
  const [assignError, setAssignError] = useState(false);
  const [assignRetry, setAssignRetry] = useState(0);
  const [assignSearching, setAssignSearching] = useState(false);

  const isSystem = !!role?.isSystem;
  const canManage = canEdit && !isSystem; // add / assign users — never on the locked system role

  const loadUsers = useCallback(async () => {
    if (!role?.id) return;
    setLoading(true);
    try { const data = await api.get(`/admin/roles/${role.id}/users`); setUsersError(false); setUsers(data.users || []); }
    catch { setUsersError(true); toast.error('Failed to load users'); }
    finally { setLoading(false); }
  }, [role?.id, toast]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  // Refetch from the server after every mutation so a failed action never leaves optimistic state behind.
  // Email-bearing actions (reset password) answer 200 with the delivery contract
  // { emailSent, emailSuppressed } — "did not throw" is NOT "the mail went out", so a
  // suppressed send gets a warning toast with its own copy and never the success one.
  // Actions that send no email carry neither flag and fall straight through.
  const runAction = async (userId, fn, successMsg) => {
    setBusyUserId(userId);
    try {
      const data = await fn();
      if (data?.emailSuppressed) toast.warning('Email SUPPRESSED (sandbox) — not delivered');
      else if (data?.emailSent === false) toast.error(data.message || 'Action completed, but the email failed to send.');
      else if (successMsg) toast.success(successMsg);
      await loadUsers();
      onChanged?.();
    } catch (err) {
      toast.error(err.message || 'Action failed');
    } finally {
      setBusyUserId(null);
      setConfirm(null);
    }
  };

  const reactivate = (u) => runAction(u.id, () => api.post(`/admin/roles/${role.id}/users/${u.id}/reactivate`), 'User reactivated');

  // Resend is NOT run through runAction: a delivery failure comes back as a 200
  // with emailSent:false, so a plain "did not throw" would toast success on an
  // email that never left the building. The token IS regenerated either way, so
  // the list is refreshed on both branches.
  const resendInvite = async (u) => {
    setBusyUserId(u.id);
    try {
      const data = await api.post(`/admin/roles/${role.id}/resend-invite`, { userId: u.id });
      if (data?.emailSuppressed) toast.warning('Email SUPPRESSED (sandbox) — not delivered');
      else if (data?.emailSent === false) toast.error(data.message || 'Invitation regenerated, but the email failed to send. Try again.');
      else toast.success('Invitation resent');
      await loadUsers();
      onChanged?.();
    } catch (err) {
      toast.error(err.message || 'Action failed');
    } finally {
      setBusyUserId(null);
    }
  };

  const runConfirm = () => {
    const u = confirm.user;
    if (confirm.kind === 'deactivate') return runAction(u.id, () => api.post(`/admin/roles/${role.id}/users/${u.id}/deactivate`), CONFIRM.deactivate.success);
    if (confirm.kind === 'remove') return runAction(u.id, () => api.delete(`/admin/roles/${role.id}/users/${u.id}`), CONFIRM.remove.success);
    if (confirm.kind === 'reset') return runAction(u.id, () => api.post(`/admin/roles/${role.id}/users/${u.id}/reset-password`), CONFIRM.reset.success);
  };

  // Invite/resend effects live here (panel = effect boundary); InviteUserModal owns
  // only the form + which inline state to render. The row is created on BOTH the
  // sent and not-sent branches, so refresh whenever the call did not throw.
  const inviteUser = async (form) => {
    const data = await api.post(`/admin/roles/${role.id}/users`, form);
    await loadUsers();
    onChanged?.();
    return data;
  };

  const resendForUser = async (userId) => {
    const data = await api.post(`/admin/roles/${role.id}/resend-invite`, { userId });
    await loadUsers();
    onChanged?.();
    return data;
  };

  // RECLAIMABLE → ONE call replaces the assign → reactivate → resend-invite
  // sequence the admin used to have to run by hand. Refreshed on every
  // non-throwing branch for the same reason as invite: a 200 with
  // emailSent:false still means the row was assigned and reactivated.
  const reclaimUser = async (userId) => {
    const data = await api.post(`/admin/roles/${role.id}/users/${userId}/reclaim`, {});
    await loadUsers();
    onChanged?.();
    return data;
  };

  // EMAIL_EXISTS → hand the address straight to the assign flow so the admin
  // never has to retype it (and never creates a duplicate account).
  const assignExistingFor = (email) => {
    setShowInvite(false);
    setAssignResults([]);
    setAssignQuery(email);
    setShowAssign(true);
  };

  // Debounced existing-user search for the assign flow (excludes users already in this role).
  useEffect(() => {
    if (!showAssign) return undefined;
    const q = assignQuery.trim();
    if (q.length < 2) { setAssignResults([]); setAssignError(false); setAssignSearching(false); return undefined; }
    setAssignSearching(true);
    const timer = setTimeout(async () => {
      try {
        const data = await api.get(`/admin/members/search?q=${encodeURIComponent(q)}`);
        const existing = new Set(users.map((u) => u.id));
        setAssignError(false);
        setAssignResults((data.results || []).filter((r) => !existing.has(r.id)));
      } catch { setAssignError(true); setAssignResults([]); }
      finally { setAssignSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [assignQuery, showAssign, users, assignRetry]);

  const assignExisting = async (u) => {
    setBusyUserId(u.id);
    try {
      await api.post('/admin/roles/assign', { userId: u.id, roleId: role.id });
      toast.success('User assigned to role'); setShowAssign(false); setAssignQuery(''); setAssignResults([]);
      await loadUsers(); onChanged?.();
    } catch (err) { toast.error(err.message || 'Failed to assign user'); }
    finally { setBusyUserId(null); }
  };

  const openAssign = () => { setAssignQuery(''); setAssignResults([]); setAssignError(false); setShowAssign(true); };

  const confirmBusy = !!confirm && busyUserId === confirm.user.id;
  const meta = confirm ? CONFIRM[confirm.kind] : null;
  const targetName = confirm ? (fullName(confirm.user) || confirm.user.email) : '';
  const protectTip = 'Super Admin accounts are protected';

  return (
    <div className={styles.usersPanel}>
      <div className={styles.usersPanelHeader}>
        <span className={styles.usersPanelTitle}>Assigned Users ({users.length})</span>
        {canManage && (
          <div className={styles.usersPanelActions}>
            <Button size="small" variant="secondary" onClick={openAssign}>Assign Existing</Button>
            <Button size="small" onClick={() => setShowInvite(true)}>+ Invite User</Button>
          </div>
        )}
      </div>

      {loading ? (
        <div className={styles.usersLoading}>Loading users…</div>
      ) : usersError && users.length === 0 ? (
        <ListLoadError message="Could not load users" onRetry={() => loadUsers()} />
      ) : users.length === 0 ? (
        <div className={styles.usersEmpty}>No users assigned to this role yet.</div>
      ) : (
        <div className={styles.userRows}>
          {users.map((u) => {
            const badge = statusBadge(u);
            const busy = busyUserId === u.id;
            const pending = isPendingInvite(u);
            return (
              <div key={u.id} className={styles.userRow}>
                <div className={styles.userRowMain}>
                  <InitialsAvatar firstName={u.firstName} lastName={u.lastName} color={role.color || '#8b5cf6'} size="md" />
                  <div className={styles.userRowInfo}>
                    <div className={styles.userRowTop}>
                      <span className={styles.userRowName}>{fullName(u) || u.email}</span>
                      <span className={`${styles.statusBadge} ${badge.cls}`}>{badge.label}</span>
                    </div>
                    <span className={styles.userRowEmail}>{u.email}</span>
                    <span className={styles.userRowMeta}>{u.role || role.name}{u.createdAt ? ` · Added ${formatDate(u.createdAt)}` : ''}</span>
                  </div>
                </div>
                {canEdit && (
                  <div className={styles.userRowActions}>
                    {busy && <span className={styles.rowSpinner} aria-hidden="true" />}
                    {pending ? (
                      <button className={styles.userActionBtn} disabled={busy} onClick={() => resendInvite(u)} title="Resend invitation email">Resend</button>
                    ) : (
                      <button className={styles.userActionBtn} disabled={busy || isSystem} onClick={() => setConfirm({ kind: 'reset', user: u })} title={isSystem ? protectTip : 'Send a password reset email'}>Reset Password</button>
                    )}
                    {!pending && (u.isActive
                      ? <button className={styles.userActionBtn} disabled={busy || isSystem} onClick={() => setConfirm({ kind: 'deactivate', user: u })} title={isSystem ? protectTip : 'Deactivate this user'}>Deactivate</button>
                      : <button className={styles.userActionBtn} disabled={busy || isSystem} onClick={() => reactivate(u)} title={isSystem ? protectTip : 'Reactivate this user'}>Reactivate</button>
                    )}
                    <button className={`${styles.userActionBtn} ${styles.userActionDanger}`} disabled={busy || isSystem} onClick={() => setConfirm({ kind: 'remove', user: u })} title={isSystem ? protectTip : 'Remove from this role'}>Remove</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Modal isOpen={!!confirm} onClose={() => { if (!confirmBusy) setConfirm(null); }} title={meta?.title || ''}>
        <div className={styles.confirmDialog}>
          {confirm?.kind === 'deactivate' && <p>Deactivate <strong>{targetName}</strong>? They will immediately lose admin access and be signed out on their next request.</p>}
          {confirm?.kind === 'remove' && <p>Remove <strong>{targetName}</strong> from {role?.name}? Their account stays active but loses this role&apos;s access.</p>}
          {confirm?.kind === 'reset' && <p>Send a password reset email to <strong>{confirm.user.email}</strong>? They will receive a secure link to set a new password. No password or link is shown here.</p>}
          <div className={styles.formActions}>
            <Button variant="secondary" disabled={confirmBusy} onClick={() => setConfirm(null)}>Cancel</Button>
            <Button variant={meta?.variant || 'primary'} loading={confirmBusy} onClick={runConfirm}>{meta?.label || 'Confirm'}</Button>
          </div>
        </div>
      </Modal>

      <InviteUserModal
        isOpen={showInvite}
        roleName={role?.name}
        onClose={() => setShowInvite(false)}
        onInvite={inviteUser}
        onResend={resendForUser}
        onReclaim={reclaimUser}
        onAssignExisting={assignExistingFor}
      />

      <Modal isOpen={showAssign} onClose={() => setShowAssign(false)} title={`Assign Existing User to ${role?.name || 'Role'}`}>
        <div className={styles.form}>
          <div className={styles.formGroup}>
            <label className={styles.label}>Search by name or email</label>
            <Input value={assignQuery} onChange={(e) => setAssignQuery(e.target.value)} placeholder="Type at least 2 characters…" />
            <span className={styles.hint}>Assigning replaces the user&apos;s current role with {role?.name}.</span>
          </div>
          <div className={styles.searchResults}>
            {assignSearching && <div className={styles.usersLoading}>Searching…</div>}
            {!assignSearching && assignError && (
              <div className={styles.searchError} role="alert">
                <span>Could not search users. This is not the same as finding no match.</span>
                <button type="button" className={styles.searchRetry} onClick={() => setAssignRetry((n) => n + 1)}>Try again</button>
              </div>
            )}
            {!assignSearching && !assignError && assignQuery.trim().length >= 2 && assignResults.length === 0 && <div className={styles.usersEmpty}>No matching users found.</div>}
            {assignResults.map((r) => (
              <button key={r.id} className={styles.searchResultItem} disabled={busyUserId === r.id} onClick={() => assignExisting(r)}>
                <InitialsAvatar firstName={r.firstName} lastName={r.lastName} color={role.color || '#8b5cf6'} size="sm" />
                <span className={styles.searchResultName}>{fullName(r) || r.email}</span>
                <span className={styles.searchResultEmail}>{r.email}</span>
              </button>
            ))}
          </div>
        </div>
        <div className={styles.formActions}><Button variant="secondary" onClick={() => setShowAssign(false)}>Close</Button></div>
      </Modal>
    </div>
  );
}
